import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useCurrency } from '../hooks/useCurrency';
import { Container } from '../components/ui/Container';
import type { Order, OrderBundle } from '../types';

const API_URL = import.meta.env.VITE_API_URL as string;

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, { dot: string; badge: string; label: string }> = {
  pending:    { dot: 'bg-amber-400',   badge: 'bg-amber-50 text-amber-700 border-amber-200',   label: 'Pending' },
  processing: { dot: 'bg-blue-400',    badge: 'bg-blue-50 text-blue-700 border-blue-200',       label: 'Processing' },
  shipped:    { dot: 'bg-indigo-400',  badge: 'bg-indigo-50 text-indigo-700 border-indigo-200', label: 'Shipped' },
  completed:  { dot: 'bg-emerald-400', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Completed' },
  cancelled:  { dot: 'bg-red-400',     badge: 'bg-red-50 text-red-700 border-red-200',           label: 'Cancelled' },
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_STYLES[status] ?? { dot: 'bg-stone', badge: 'bg-stone-light text-stone border-stone-light', label: status };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold capitalize ${s.badge}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

// ─── Bundle Row (in expanded order) ───────────────────────────────────────────

function MyOrderBundleRow({ bundle, format }: { bundle: OrderBundle; format: (v: number) => string }) {
  const src = bundle.bundle_image ? (bundle.bundle_image.startsWith('http') ? bundle.bundle_image : `${API_URL}${bundle.bundle_image}`) : null;
  return (
    <div className="rounded-xl border border-amber-200/60 bg-amber-50/30 p-3">
      <div className="flex items-center gap-3">
        <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-amber-200 bg-amber-50">
          {src
            ? <img src={src} alt={bundle.bundle_name} className="h-full w-full object-cover" />
            : <span className="flex h-full w-full items-center justify-center text-lg">📦</span>
          }
          <span className="absolute -right-1 -top-1 flex h-4.5 w-4.5 items-center justify-center rounded-full bg-ink text-[9px] font-bold text-cream">
            {bundle.quantity}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-ink leading-snug">{bundle.bundle_name}</p>
          <p className="mt-0.5 text-xs text-stone">{bundle.products.length} {bundle.products.length === 1 ? 'product' : 'products'} included</p>
        </div>
        <p className="shrink-0 text-sm font-medium text-ink tabular-nums">{format(Number(bundle.line_total))}</p>
      </div>
      <div className="mt-2 ml-17 space-y-1">
        {bundle.products.map((p, i) => (
          <p key={i} className="truncate text-xs text-stone/80">
            ✓ {p.product_name} — {[p.size_label, p.frame_name, p.material_name].filter(Boolean).join(' · ') || 'Standard'}
          </p>
        ))}
      </div>
      {Number(bundle.discount_amount) > 0 && (
        <p className="mt-2 text-[11px] font-medium text-moss">
          Bundle saved you {format(Number(bundle.discount_amount) * bundle.quantity)}
        </p>
      )}
    </div>
  );
}

// ─── Order Card ───────────────────────────────────────────────────────────────

function OrderCard({ order }: { order: Order }) {
  const { format } = useCurrency();
  const [expanded, setExpanded] = useState(false);

  const addr = order.shipping_address as Record<string, string> | null;
  const addressLine = [addr?.address_line1, addr?.area, addr?.city].filter(Boolean).join(', ');

  return (
    <div className="rounded-2xl border border-stone-light bg-paper overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full text-left px-5 py-4 hover:bg-cream/50 transition-colors"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs font-bold text-ink bg-stone-light/60 px-2 py-0.5 rounded-md">
                {order.order_number}
              </span>
              <StatusBadge status={order.status} />
            </div>
            <p className="mt-1.5 text-xs text-stone">
              {new Date(order.created_at ?? '').toLocaleDateString('en-GB', {
                day: 'numeric', month: 'long', year: 'numeric',
              })}
              {addressLine ? ` · ${addressLine}` : ''}
            </p>
          </div>
          <div className="shrink-0 text-right">
            <p className="text-base font-semibold text-ink tabular-nums">{format(Number(order.grand_total))}</p>
            <p className="text-[11px] text-stone">{(order.items?.length ?? 0) + (order.bundles?.length ?? 0)} item{((order.items?.length ?? 0) + (order.bundles?.length ?? 0)) !== 1 ? 's' : ''}</p>
          </div>
        </div>

        {/* Item image strip */}
        <div className="mt-3 flex gap-2">
          {(order.items ?? []).slice(0, 5).map((item) => {
            const path = (item as { image_path?: string }).image_path;
            const src = path ? (path.startsWith('http') ? path : `${API_URL}${path}`) : null;
            return (
              <div key={item.id} className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-stone-light bg-cream">
                {src
                  ? <img src={src} alt={item.product_name ?? ''} className="h-full w-full object-cover" />
                  : <span className="flex h-full w-full items-center justify-center text-[10px] text-stone">#{item.product_id}</span>
                }
              </div>
            );
          })}
          {(order.bundles ?? []).slice(0, 5 - (order.items?.length ?? 0)).map((bundle) => {
            const src = bundle.bundle_image ? (bundle.bundle_image.startsWith('http') ? bundle.bundle_image : `${API_URL}${bundle.bundle_image}`) : null;
            return (
              <div key={`b-${bundle.id}`} className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-amber-200 bg-amber-50">
                {src
                  ? <img src={src} alt={bundle.bundle_name} className="h-full w-full object-cover" />
                  : <span className="flex h-full w-full items-center justify-center text-[10px] text-amber-600">📦</span>
                }
              </div>
            );
          })}
          {((order.items?.length ?? 0) + (order.bundles?.length ?? 0)) > 5 && (
            <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-stone-light bg-cream text-[11px] font-semibold text-stone">
              +{((order.items?.length ?? 0) + (order.bundles?.length ?? 0)) - 5}
            </div>
          )}
          <div className="ml-auto flex items-center gap-1 text-[11px] text-stone">
            <span>{expanded ? 'Hide details' : 'View details'}</span>
            <svg
              className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <polyline points="6 9 12 15 18 9" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
        </div>
      </button>

      {/* Expanded details */}
      <div className={`grid transition-all duration-300 ease-in-out ${expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden">
          <div className="border-t border-stone-light/60 px-5 py-5 space-y-5">

            {/* Items */}
            <div>
              <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-stone">Items Ordered</p>
              <div className="space-y-3">
                {(order.items ?? []).map((item) => {
                  const path = (item as { image_path?: string }).image_path;
                  const src = path ? (path.startsWith('http') ? path : `${API_URL}${path}`) : null;
                  const attrs = [
                    (item as { size_label?: string }).size_label,
                    (item as { frame_name?: string }).frame_name,
                    (item as { material_name?: string }).material_name,
                  ].filter(Boolean).join(' · ');

                  return (
                    <div key={item.id} className="flex items-center gap-3">
                      <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-stone-light bg-cream">
                        {src
                          ? <img src={src} alt={item.product_name ?? ''} className="h-full w-full object-cover" />
                          : <span className="flex h-full w-full items-center justify-center text-[10px] text-stone">#{item.product_id}</span>
                        }
                        <span className="absolute -right-1 -top-1 flex h-4.5 w-4.5 items-center justify-center rounded-full bg-ink text-[9px] font-bold text-cream">
                          {item.quantity}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink leading-snug">{item.product_name ?? `Product #${item.product_id}`}</p>
                        {attrs && <p className="mt-0.5 text-xs text-stone">{attrs}</p>}
                      </div>
                      <p className="shrink-0 text-sm font-medium text-ink tabular-nums">
                        {format(Number(item.unit_price) * item.quantity)}
                      </p>
                    </div>
                  );
                })}
                {(order.bundles ?? []).map((bundle) => (
                  <MyOrderBundleRow key={`bundle-${bundle.id}`} bundle={bundle} format={format} />
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="rounded-xl border border-stone-light bg-cream/50 px-4 py-3 space-y-2">
              <div className="flex justify-between text-sm text-stone">
                <span>Subtotal</span><span className="tabular-nums">{format(Number(order.subtotal))}</span>
              </div>
              {Number(order.discount_total) > 0 && (
                <div className="flex justify-between text-sm text-moss">
                  <span>Discount</span>
                  <span className="tabular-nums">−{format(Number(order.discount_total))}</span>
                </div>
              )}
              {Number(order.shipping_total) > 0 && (
                <div className="flex justify-between text-sm text-stone">
                  <span>Shipping</span><span className="tabular-nums">{format(Number(order.shipping_total))}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-stone-light pt-2 text-sm font-semibold text-ink">
                <span>Total</span><span className="tabular-nums">{format(Number(order.grand_total))}</span>
              </div>
            </div>

            {/* Shipping address */}
            {addr && (
              <div>
                <p className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-stone">Shipped To</p>
                <div className="text-sm text-ink-soft leading-relaxed">
                  <p className="font-medium text-ink">{order.customer_name}</p>
                  {addr.address_line1 && <p>{addr.address_line1}</p>}
                  {addr.address_line2 && <p>{addr.address_line2}</p>}
                  {(addr.area || addr.city) && <p>{[addr.area, addr.city].filter(Boolean).join(', ')}</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── My Orders Page ───────────────────────────────────────────────────────────

export function MyOrdersPage() {
  const { format } = useCurrency();
  const [email, setEmail] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');

  const { data: orders, isLoading, isError, isFetching } = useQuery<Order[]>({
    queryKey: ['my-orders', submittedEmail],
    queryFn: async () => {
      const res = await api.get<{ data: Order[] }>(`/orders?email=${encodeURIComponent(submittedEmail)}`);
      return res.data.data ?? res.data;
    },
    enabled: !!submittedEmail,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) setSubmittedEmail(email.trim());
  };

  const totalSpent = (orders ?? []).reduce((sum, o) => sum + Number(o.grand_total), 0);

  return (
    <Container className="py-14 md:py-20 max-w-2xl">
      {/* Title */}
      <div className="mb-10">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone mb-3">Order History</p>
        <h1 className="font-serif text-4xl font-light text-ink leading-tight">Your Orders</h1>
        <p className="mt-3 text-sm text-stone leading-relaxed">
          Enter the email address you used at checkout to view all your orders and their details.
        </p>
      </div>

      {/* Email lookup form */}
      <form onSubmit={handleSubmit} className="flex gap-2 mb-10">
        <div className="relative flex-1">
          <svg className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-stone" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <input
            type="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="your@email.com"
            autoComplete="email"
            className="w-full rounded-xl border border-stone-light bg-paper pl-10 pr-4 py-3 text-sm text-ink placeholder:text-stone/60 focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/10 transition-colors"
          />
        </div>
        <button
          type="submit"
          disabled={isFetching}
          className="shrink-0 rounded-xl bg-ink px-5 py-3 text-sm font-semibold text-cream hover:bg-clay disabled:opacity-50 transition-colors"
        >
          {isFetching ? (
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          ) : 'Look up'}
        </button>
      </form>

      {/* Results */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <svg className="animate-spin h-6 w-6 text-stone" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        </div>
      )}

      {isError && (
        <div className="rounded-xl border border-clay/30 bg-clay/5 px-4 py-3 text-sm text-clay">
          Something went wrong. Please try again.
        </div>
      )}

      {!isLoading && orders && (
        <>
          {orders.length === 0 ? (
            <div className="py-16 text-center">
              <svg className="mx-auto mb-4 h-12 w-12 text-stone-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-sm font-medium text-ink">No orders found</p>
              <p className="mt-1 text-xs text-stone">We couldn't find any orders for <strong>{submittedEmail}</strong>.</p>
            </div>
          ) : (
            <>
              {/* Summary bar */}
              <div className="mb-6 flex items-center justify-between rounded-xl border border-stone-light bg-paper px-5 py-3">
                <div className="flex items-center gap-2">
                  <svg className="h-4 w-4 text-stone" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="text-sm text-ink-soft">{submittedEmail}</span>
                </div>
                <div className="flex items-center gap-4 text-xs text-stone">
                  <span><strong className="text-ink">{orders.length}</strong> order{orders.length !== 1 ? 's' : ''}</span>
                  <span className="text-stone-light">·</span>
                  <span>Spent <strong className="text-ink">{format(totalSpent)}</strong></span>
                </div>
              </div>

              {/* Order cards */}
              <div className="space-y-3">
                {orders.map(order => (
                  <OrderCard key={order.id} order={order} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </Container>
  );
}
